import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight, MapPin, Phone, Star, Calendar, Scissors, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


import { SiteShell } from "@/components/layout/SiteShell";
import { SalonCard } from "@/components/SalonCard";
import { InternalLinkHub } from "@/components/InternalLinkHub";


import { ReviewsBlock } from "@/components/ReviewsBlock";
import { Faq } from "@/components/Faq";
import { salons } from "@/data/salons";
import { reviewsForSalon } from "@/data/reviews";


import { articles } from "@/data/blog";
import { homepageFaq } from "@/data/homepage-faq";
import { getOpenStatus } from "@/lib/openHours";
import { getSalonCtas } from "@/lib/cta";
import { track } from "@/lib/analytics";
import { withBookingUtm, REVIEW_COUNT_TEXT, REVIEW_COUNT_VALUE, GENERIC_BOKADIREKT } from "@/lib/booking";
import {
  faqSchema,
  localBusinessSchema,
  organizationSchema,
  websiteSchema,
  jsonLd,
  languageAlternates,
  absUrl,
} from "@/lib/seo";
import { SalonsMapClient } from "@/components/SalonsMapClient";
import { SalonPickerSheet } from "@/components/SalonPickerSheet";
import { BookAgainBlock } from "@/components/BookAgainBlock";
import { BarberGrid } from "@/components/BarberGrid";
import heroAsset from "@/assets/opt/hero-barber.webp";
import heroAsset900 from "@/assets/opt/hero-barber-900.webp";
import arningeInneAsset from "@/assets/opt/arninge-inne.webp";
import ahlensUtsidaAsset from "@/assets/opt/ahlens-utsida.webp";
import ahlensTvattAsset from "@/assets/opt/ahlens-tvatt.webp";
import akersbergaSalongAsset from "@/assets/opt/akersberga-salong.webp";
import herrklippningAsset from "@/assets/opt/herrklippning-tailors-barber.webp";
import taperFadeAsset from "@/assets/opt/taper-fade-tailors-barber.webp";
import skaggAsset from "@/assets/opt/skagg-tailors-barber.webp";
import klippningSkaggAsset from "@/assets/opt/klippning-skagg-tailors-barber.webp";
import barnklippningAsset from "@/assets/opt/barnklippning-tailors-barber.webp";
import seniorklippningAsset from "@/assets/opt/pensionarsklippning-tailors-barber.webp";
import { SALON_BARBERARE } from "@/data/barbers";
import { internalPath } from "@/lib/paths";
import { SalonRatingLines } from "@/components/SalonRatingLines";
const heroImg = heroAsset;
const heroImg900 = heroAsset900;
const HERO_SRCSET = `${heroAsset900} 900w, ${heroAsset} 1440w`;
const HERO_SIZES = "(max-width: 768px) 100vw, 60vw";
const midImg = arningeInneAsset;

const BOKA_HELA_SVERIGE = GENERIC_BOKADIREKT;

const HOMEPAGE_TITLE = "Barberare Stockholm: 4 salonger & drop-in | Tailor's Barber";
const HOMEPAGE_DESCRIPTION =
  "Barberare i Stockholm på fyra salonger: Åhléns City, Täby, Åkersberga och Arninge. Herrklippning 450 kr, skin fade, taper fade och skägg. Boka online eller drop-in.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: HOMEPAGE_TITLE },
      { name: "description", content: HOMEPAGE_DESCRIPTION },
      { property: "og:title", content: HOMEPAGE_TITLE },
      { property: "og:description", content: HOMEPAGE_DESCRIPTION },
      { property: "og:url", content: absUrl("/") },
    ],
    links: [
      { rel: "canonical", href: absUrl("/") },
      {
        rel: "preload",
        as: "image",
        href: heroImg900,
        imageSrcSet: HERO_SRCSET,
        imageSizes: HERO_SIZES,
        fetchpriority: "high",
      },
      ...languageAlternates("/", "/en"),
    ],
    scripts: [
      jsonLd(organizationSchema()),
      jsonLd(websiteSchema()),
      jsonLd(faqSchema(homepageFaq)),
      ...salons.map((s) => jsonLd(localBusinessSchema(s, reviewsForSalon(s.slug)))),
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <SiteShell>
      <Hero />
      <BookAgainBlock />
      <LocationsSection />
      <ReviewsBlock />
      <ServicesGrid />
      <TeamSection />
      <SalonsMapSection />
      <DropInBlock />
      <WhyUs />
      <BrandIntro />
      <BlogPreview />
      <InternalLinkHub />

      <Faq items={homepageFaq} />
      <FinalCta />
    </SiteShell>
  );
}



function Hero() {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <section className="relative min-h-[80vh] md:min-h-[92vh] flex items-center overflow-hidden bg-leather-deep">
      {/* Background image */}
      <img
        src={heroImg900}
        srcSet={HERO_SRCSET}
        sizes={HERO_SIZES}
        decoding="async"
        alt="Barberare Stockholm klipper taper fade hos Tailor's Barber"
        width={1280}
        height={1908}
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover object-center opacity-90 scale-[1.15] translate-y-[15%] translate-x-[6%] md:scale-[1.08] md:translate-y-0 md:translate-x-[8%]"
      />

      {/* Angled cream editorial overlay */}
      <div
        className="absolute inset-0 md:inset-y-0 md:left-0 md:w-[68%] bg-background z-10 hero-overlay"
        aria-hidden="true"
      />

      {/* Hero content below */}

      {/* Vertical brand detail on the image side */}
      <div className="hidden md:flex absolute bottom-10 right-8 lg:right-14 z-20 flex-col items-center gap-6">
        <div className="h-32 w-px bg-cream/25" />
        <p
          className="text-[10px] text-cream/60 uppercase tracking-[0.4em] font-semibold"
          style={{ writingMode: "vertical-lr" }}
        >
          Hantverk & Precision
        </p>
      </div>

      {/* Content anchored to the cream side */}
      <div className="relative z-20 w-full container-page pt-16 md:pt-16 pb-6 md:pb-12">
        <div className="max-w-2xl md:max-w-[838px] md:pr-6">
          {/* Trust eyebrow with stars */}
          <div className="flex items-center gap-3 mb-4 md:mb-5">
            <div className="flex gap-0.5 text-brass" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={12} className="fill-brass text-brass" />
              ))}
            </div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-foreground/60 font-semibold">
              {REVIEW_COUNT_TEXT}
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-foreground">
            Stockholms{" "}
            <span className="italic text-brass-deep font-light">barbersalong</span>,{" "}Tailor&apos;s Barber.
          </h1>

          {/* Subhead */}
          <p className="hidden md:block mt-4 max-w-[814px] text-lg text-foreground/70 leading-relaxed">
            Barberare i Stockholm med herrklippning, taper fade, skin fade och skäggtrimning på fyra
            salonger: Åhléns City, Täby Centrum, Åkersberga och Arninge. Boka online eller kom på drop-in.
          </p>

          {/* CTAs — desktop only, MobileBottomBar handles mobile */}
          <div className="hidden md:flex mt-6 flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => {
                track("book_now_click", { cta_location: "hero_primary" });
                setPickerOpen(true);
              }}
              className="inline-flex items-center justify-center gap-2 bg-ink text-cream px-10 py-4 text-[13px] font-bold tracking-[0.15em] uppercase hover:bg-brass hover:text-ink transition-colors duration-300"
            >
              Boka tid <ArrowRight size={16} />
            </button>
            <a
              href="#salonger"
              className="inline-flex items-center justify-center border border-foreground/20 text-foreground px-10 py-4 text-[13px] font-bold tracking-[0.15em] uppercase hover:border-foreground transition-colors duration-300"
            >
              Hitta salong
            </a>
            <span className="w-full text-xs text-foreground/60 md:w-auto md:ml-2">
              Från 450 kr · 30 min
            </span>
          </div>
          <SalonPickerSheet open={pickerOpen} action="book" onClose={() => setPickerOpen(false)} />
        </div>

        {/* Salon pills — mobile/tablet: 2×2 grid, desktop: one row */}
        <div className="mt-4 md:mt-5">
          <div className="eyebrow text-foreground/60 mb-2 md:mb-3">Boka tid – välj salong:</div>
          <div className="max-w-[520px] xl:max-w-[720px]">
            <div className="grid grid-cols-2 xl:grid-cols-4 items-stretch gap-2 xl:gap-3">
              {salons.map((s) => (
                <a
                  key={s.slug}
                  href={withBookingUtm(s.bokaDirektUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("book_now_click", {
                      salon_name: s.shortName,
                      cta_location: "hero_salon_pill",
                    })
                  }
                  className="group flex min-w-0 flex-col gap-1 rounded border border-border/70 bg-card/80 px-2.5 py-2 text-left text-[10px] font-medium leading-tight text-foreground/80 backdrop-blur-sm transition-all hover:border-brass hover:bg-card hover:text-foreground hover:shadow-sm"
                >
                  <span className="flex min-w-0 items-center gap-1 font-semibold">
                    <Star size={10} className="shrink-0 fill-brass text-brass" />
                    <span className="truncate">{s.shortName}</span>
                  </span>
                  <SalonRatingLines
                    salon={s.slug}
                    compact
                    className="text-[10px] text-muted-foreground"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SalonsMapSection() {
  return (
    <section className="hidden md:block container-page py-16 md:py-24 border-t border-border" aria-labelledby="map-heading">
      <div className="max-w-2xl mb-8">
        <div className="eyebrow">Karta</div>
        <h2 id="map-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">Hitta din salong</h2>
        <p className="mt-3 text-muted-foreground">
          Fyra salonger i Stockholm — klicka på en pin för att se öppettider, betyg och boka tid.
        </p>
      </div>
      <SalonsMapClient />
    </section>
  );
}

function BrandIntro() {
  return (
    <section className="container-page py-14 md:py-20 border-t border-border" aria-labelledby="brand-intro">
      <div className="max-w-3xl">
        <div className="eyebrow">Om oss</div>
        <h2 id="brand-intro" className="mt-3 text-3xl md:text-4xl lg:text-5xl">
          Tailor&apos;s Barber – Premium barbersalong i Stockholm sedan starten
        </h2>
        <div className="mt-5 space-y-4 text-base md:text-lg text-foreground/80">
          <p>
            Tailor&apos;s Barber är en premium barbersalong med fyra salonger i
            Stockholm. Vi kombinerar traditionellt barbershantverk med modern
            stil och erbjuder <strong>herrklippning</strong>,{" "}
            <strong>taper fade</strong>, <strong>skin fade</strong>,{" "}
            <strong>skäggtrimning</strong> och grooming-behandlingar för män.
            Våra barberare är specialiserade på herrklippning och fadetekniker –
            med fokus på precision, stil och ett professionellt bemötande.
          </p>
          <p>
            Vi finns på{" "}
            <Link to={internalPath("/barberare-stockholm-ahlens-city/")} className="underline font-medium">
              Åhléns City
            </Link>{" "}
            i centrala Stockholm,{" "}
            <strong>Täby Centrum</strong>, <strong>Åkersberga</strong> (ICA Maxi)
            och <strong>Arninge</strong> (ICA Maxi).
          </p>
          <p>
            Söker du en <Link to={internalPath("/barberare-stockholm-ahlens-city/")} className="underline font-medium">Barber Stockholm</Link>{" "}
            i city eller en <Link to={internalPath("/barberare-stockholm-ahlens-city/")} className="underline font-medium">Barberare Stockholm City</Link>{" "}
            nära T-Centralen? Vår salong på Åhléns City ligger på Klarabergsgatan 50, plan 3 —
            ett minut från Centralstationen.
          </p>
        </div>
      </div>
    </section>
  );
}

const HOMEPAGE_SERVICES: Array<{
  name: string;
  price: string;
  href: string;
  desc: string;
  image: string;
  alt: string;
}> = [
  {
    name: "Herrklippning",
    price: "450 kr",
    href: "/herrklippning",
    desc: "Klassisk herrklippning med konsultation och styling.",
    image: herrklippningAsset,
    alt: "Herrklippning hos Tailor's Barber i Stockholm",
  },
  {
    name: "Taper Fade / Skin Fade",
    price: "450 kr",
    href: "/taper-fade",
    desc: "Alla typer av fade – low, mid, high, skin och bald fade.",
    image: taperFadeAsset,
    alt: "Taper fade klippt av barberare Stockholm hos Tailor's Barber",
  },
  {
    name: "Skäggtrimning",
    price: "380 kr",
    href: "/skaggtrimning",
    desc: "Formning och trimning av skägg för ett polerat utseende.",
    image: skaggAsset,
    alt: "Skäggtrimning hos Tailor's Barber i Stockholm",
  },
  {
    name: "Herrklippning + Skägg",
    price: "680 kr",
    href: "/herrklippning-skaggtrimning",
    desc: "Komplett behandling – klippning och skägg i ett paket.",
    image: klippningSkaggAsset,
    alt: "Herrklippning och skäggtrimning Stockholm hos Tailor's Barber",
  },
  {
    name: "Barnklippning",
    price: "380 kr",
    href: "/barnklippning-stockholm",
    desc: "Klippning för barn upp till 10 år.",
    image: barnklippningAsset,
    alt: "Barnklippning hos Tailor's Barber i Stockholm",
  },
  {
    name: "Seniorklippning",
    price: "270 kr",
    href: "/seniorklippning-pensionarsklippning",
    desc: "Mån–tors 10–14 på alla salonger.",
    image: seniorklippningAsset,
    alt: "Seniorklippning hos Tailor's Barber i Stockholm",
  },
];



function ServicesGrid() {
  return (
    <section className="container-page py-16 md:py-24 border-t border-border" aria-labelledby="services-heading">
      <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="eyebrow">Behandlingar</div>
          <h2 id="services-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">Våra tjänster</h2>
          <p className="mt-3 text-muted-foreground">
            Barberare i Stockholm utför herrklippning, fade och skäggvård på fyra salonger runt stan.
          </p>
        </div>
        <Link to="/priser/" className="btn-ghost">
          Se alla priser <ArrowRight size={16} />
        </Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {HOMEPAGE_SERVICES.map((svc) => (
          <a
            key={svc.name}
            href={internalPath(svc.href)}
            className="card-elev overflow-hidden flex flex-col hover:border-brass transition-colors group"
          >
            <div className="aspect-[5/3] overflow-hidden bg-muted">
              <img
                src={svc.image}
                alt={svc.alt}
                loading="lazy"
                width={800}
                height={480}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-6 flex flex-col gap-2 flex-1">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg md:text-xl">{svc.name}</h3>
                <span className="shrink-0 text-brass-deep font-semibold text-sm md:text-base">
                  {svc.price}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{svc.desc}</p>
              <span className="text-sm text-brass-deep inline-flex items-center gap-1 mt-2 group-hover:gap-2 transition-all">
                Läs mer <ArrowRight size={14} />
              </span>
            </div>
          </a>
        ))}
      </div>

    </section>
  );
}

function LocationsSection() {
  return (
    <section id="salonger" className="container-page py-16 md:py-24 border-t border-border" aria-labelledby="locations-heading">
      <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="eyebrow">Salonger</div>
          <h2 id="locations-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">
            Hitta din närmaste salong i Stockholm
          </h2>
          <p className="mt-3 text-muted-foreground">
            Fyra salonger – Täby Centrum, Åhléns City, Åkersberga och Arninge. Samma höga standard, alltid drop-in.
          </p>
          <p className="mt-2 text-sm text-foreground/80 inline-flex items-center gap-2">
            <Calendar size={14} className="text-brass-deep" />
            Boka via BokaDirekt – välj salong och tid direkt.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <a
            href={BOKA_HELA_SVERIGE}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("book_now_click", { cta_location: "locations_section_header" })}
            className="btn-primary text-sm px-5 py-3 inline-flex items-center justify-center gap-2"
          >
            Boka tid <ArrowRight size={16} />
          </a>
          <Link to="/" className="btn-ghost text-sm px-5 py-3 inline-flex items-center justify-center gap-2">
            Alla salonger <ArrowRight size={16} />
          </Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {salons.map((s) => (
          <SalonCard key={s.slug} salon={s} />
        ))}
      </div>
    </section>
  );
}

const WHY_US = [
  {
    title: "Specialiserade barberare",
    desc: "Våra barberare i Stockholm är utbildade och specialiserade på herrklippning. Inte frisörer som också klipper herr, utan dedikerade barberare.",
  },
  {
    title: "Fade-experter",
    desc: "Vi utför alla varianter av fade: taper fade, skin fade, drop fade, low, mid och high fade. Precision är vårt signum.",
  },
  {
    title: "4 salonger i Stockholm",
    desc: "Oavsett om du är i city, Täby, Åkersberga eller Arninge finns alltid en Tailor's Barber, din barberare i Stockholm, nära dig.",
  },
  {
    title: "Boka online eller drop-in",
    desc: "Onlinebokning via BokaDirekt dygnet runt. Drop-in välkommet i mån av tid på alla salonger.",
  },
  {
    title: `${REVIEW_COUNT_VALUE} nöjda kunder`,
    desc: "Betygsatta av tusentals kunder på Google och BokaDirekt. Kvalitet du kan lita på.",
  },
];

const WHY_US_ICONS = [Scissors, Star, MapPin, Calendar, Star];

function MidBanner() {
  return (
    <section className="container-page py-8 md:py-12" aria-hidden="true">
      <div className="aspect-[21/9] rounded-2xl overflow-hidden bg-muted shadow-soft">
        <img
          src={midImg}
          alt="Interiör med barberarstolar hos Tailor's Barber Arninge, barberare i Stockholm"
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="container-page py-16 md:py-24 border-t border-border" aria-labelledby="why-heading">
      <div className="max-w-2xl mb-10">
        <div className="eyebrow">Därför Tailor&apos;s</div>
        <h2 id="why-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">Varför välja Tailor&apos;s Barber?</h2>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {WHY_US.map((f, i) => {
          const Icon = WHY_US_ICONS[i];
          return (
            <div key={f.title} className="card-elev p-6 flex flex-col gap-3">
              <div className="h-10 w-10 rounded-full bg-brass/15 flex items-center justify-center text-brass-deep">
                <Icon size={20} />
              </div>
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const CONTENT_HUB = [
  {
    href: "/taper-fade",
    title: "Vad är en taper fade? Allt du behöver veta",
    eyebrow: "Guide",
  },
  {
    href: "/herrklippning",
    title: "Herrklippning i Stockholm – priser och bokning",
    eyebrow: "Herrklippning",
  },
  {
    href: "/skaggtrimning",
    title: "Skäggtrimning och skäggforming hos barberare",
    eyebrow: "Skägg",
  },
  {
    href: "/blogg",
    title: "Läs vår blogg om herrgrooming och barbering",
    eyebrow: "Blogg",
  },
];

function ContentHub() {
  return (
    <section className="container-page py-16 md:py-24 border-t border-border" aria-labelledby="hub-heading">
      <div className="max-w-2xl mb-10">
        <div className="eyebrow">Läs mer</div>
        <h2 id="hub-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">
          Mer om herrklippning och barbering i Stockholm
        </h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CONTENT_HUB.map((item) => (
          <a key={item.href} href={internalPath(item.href)} className="card-elev p-6 flex flex-col gap-3 hover:border-brass transition-colors">
            <div className="eyebrow">{item.eyebrow}</div>
            <h3 className="text-lg">{item.title}</h3>
            <span className="text-sm text-brass-deep inline-flex items-center gap-1 mt-auto">
              Läs mer <ArrowRight size={14} />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function BlogPreview() {
  const latest = [...articles]
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, 3);
  return (
    <section className="container-page py-16 md:py-24 border-t border-border" aria-labelledby="blog-heading">
      <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="eyebrow">Blogg</div>
          <h2 id="blog-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">
            Från vår blogg om herrgrooming
          </h2>
        </div>
        <Link to="/blogg/" className="btn-ghost">
          Alla artiklar <ArrowRight size={16} />
        </Link>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {latest.map((a, i) => {
          const varied = ["HERRKLIPPNING", "FADE & STIL", "SKÄGG & GROOMING"];
          const label = varied[i] ?? a.category;
          return (
            <a key={a.slug} href={internalPath(`/${a.slug}`)} className="card-elev overflow-hidden flex flex-col group">
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={a.hero}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                />
              </div>
              <div className="p-5 flex flex-col gap-2">
                <div className="eyebrow">{label}</div>
                <h3 className="text-lg leading-snug">{a.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>
                <span className="text-sm text-brass-deep inline-flex items-center gap-1 mt-2">
                  Läs artikel <ArrowRight size={14} />
                </span>
              </div>
            </a>
          );
        })}
      </div>

    </section>
  );
}

function FinalCta() {
  return (
    <section className="container-page py-16 md:py-24" aria-labelledby="final-cta">
      <div className="card-elev p-8 md:p-14 bg-ink text-cream text-center">
        <div className="eyebrow text-brass">Boka nu</div>
        <h2 id="final-cta" className="mt-3 text-3xl md:text-5xl text-cream">
          Redo för en ny look?
        </h2>
        <p className="mt-5 mx-auto max-w-xl text-cream/80 text-base md:text-lg">
          Boka din tid online på under en minut. Välj salong, barberare och tid som passar dig.
        </p>
        <div className="mt-8 flex justify-center">
          <a
            href={BOKA_HELA_SVERIGE}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("book_now_click", { cta_location: "home_final_cta" })}
            className="btn-brass text-base px-7 py-4"
          >
            Boka Online Nu <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}


function DropInBlock() {
  return (
    <section id="drop-in" className="container-page py-16 md:py-24 scroll-mt-24" aria-labelledby="dropin-heading">
      <div className="card-elev p-6 sm:p-8 md:p-14 bg-ink text-cream">
        <div className="eyebrow text-brass">Drop-in</div>
        <h2 id="dropin-heading" className="mt-3 text-3xl md:text-5xl text-cream max-w-2xl">
          Drop-in hos Tailor&apos;s Barber
        </h2>
        <p className="mt-5 max-w-2xl text-cream/80 text-base md:text-lg">
          Drop-in hos din barberare i Stockholm i mån av plats. För garanterad tid rekommenderar vi att du bokar online.
        </p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {salons.map((s) => {
            const status = getOpenStatus(s);
            return (
            <li key={s.slug} className="rounded-2xl border border-cream/15 bg-cream/5 p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-cream font-medium">{s.shortName}</div>
                  <div className="mt-0.5 text-cream/60 text-xs flex items-center gap-1">
                    <Clock size={11} />
                    {status.todayHours ? <>Idag {status.todayHours}</> : "Stängt idag"}
                  </div>
                </div>
                <span
                  className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded font-semibold ${
                    status.open ? "bg-emerald-500/20 text-emerald-300" : "bg-cream/10 text-cream/60"
                  }`}
                >
                  {status.open ? "Öppet" : "Stängt"}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {(() => {
                  const ctas = getSalonCtas(s);
                  return (
                    <>
                      <a
                        href={ctas.book.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("book_now_click", { salon_name: s.shortName, cta_location: "home_dropin" })}
                        className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg bg-brass text-ink text-[11px] font-medium"
                        aria-label={`Boka tid på ${s.shortName}`}
                      >
                        <Calendar size={16} />
                        Boka tid
                      </a>
                      <a
                        href={ctas.call.href}
                        onClick={() => track("call_click", { salon_name: s.shortName, cta_location: "home_dropin" })}
                        className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg bg-cream/10 text-cream text-[11px]"
                        aria-label={`Ring ${s.shortName} på ${s.phoneDisplay}`}
                      >
                        <Phone size={16} />
                        Ring
                      </a>
                      <a
                        href={ctas.directions.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("directions_click", { salon_name: s.shortName, cta_location: "home_dropin" })}
                        className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg bg-cream/10 text-cream text-[11px]"
                        aria-label={`Karta och vägbeskrivning till ${s.shortName}`}
                      >
                        <MapPin size={16} />
                        Karta
                      </a>
                    </>
                  );
                })()}
              </div>
            </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}




function PhotoStrip() {
  const photos = [
    {
      src: ahlensUtsidaAsset,
      alt: "Entrén till Tailor's Barber Åhléns City i Stockholm",
    },
    {
      src: akersbergaSalongAsset,
      alt: "Salongen hos Tailor's Barber Åkersberga där herrklippning och skäggtrimning utförs",
    },
    {
      src: ahlensTvattAsset,
      alt: "Hårtvättstation hos Tailor's Barber Åhléns City",
    },
  ];
  return (
    <section className="py-6 md:py-8">
      <div className="container-page">
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-4 px-4 md:mx-0 md:px-0">
          {photos.map((p) => (
            <div
              key={p.src}
              className="shrink-0 snap-start aspect-[3/4] w-32 md:w-40 rounded-xl overflow-hidden bg-muted"
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


const SALON_KEYS = ["ahlens-city", "taby", "arninge", "akersberga"] as const;

function TeamSection() {
  const [activeTab, setActiveTab] = useState("ahlens-city");
  const activeSalon = salons.find(s => s.slug === activeTab);

  return (
    <section className="container-page py-16 md:py-24 border-t border-border" aria-labelledby="team-heading">
      <div className="max-w-2xl mb-10">
        <div className="eyebrow">Teamet</div>
        <h2 id="team-heading" className="mt-3 text-3xl md:text-4xl lg:text-5xl">Våra barberare</h2>
        <p className="mt-3 text-muted-foreground">
          Välj din salong och boka din favoritbarberare i Stockholm.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-8 h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
          {SALON_KEYS.map((key) => (
            <TabsTrigger
              key={key}
              value={key}
              className="rounded-full px-4 py-2 text-sm font-medium border border-border bg-card text-foreground data-[state=active]:bg-leather data-[state=active]:text-cream data-[state=active]:border-leather transition-colors"
            >
              {SALON_BARBERARE[key].name}
            </TabsTrigger>
          ))}
        </TabsList>

        {SALON_KEYS.map((key) => {
          const { name, barbers } = SALON_BARBERARE[key];
          return (
            <TabsContent key={key} value={key}>
              <BarberGrid barbers={barbers} salonName={name} />
            </TabsContent>
          );
        })}
        {activeSalon && (
          <div className="mt-8 flex justify-start">
            <a
              href={withBookingUtm(activeSalon.bokaDirektUrl)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("book_now_click", { salon_name: activeSalon.shortName, cta_location: "team_section_cta" })}
              className="btn-brass inline-flex items-center gap-2"
            >
              Boka din tid hos {activeSalon.shortName} <ArrowRight size={16} />
            </a>
          </div>
        )}
      </Tabs>
    </section>
  );
}
